<script lang="ts">
  import './redteam.css';
  import AttackField from './AttackField.svelte';
  import { createRun } from './run.svelte';
  import { azimuthStats } from './azimuth-stats';
  import { revisions, scenarioCount, scenarios, openGaps, atRisk, usd } from './gaps';
  import { trackCtaClick } from '$lib/utils/analytics';

  let { id }: { id?: string } = $props();
  let ver = $state(0);
  let approved = $state(false);
  let history = $state<number[]>([]);
  const run = createRun(scenarioCount, 1600);
  let open = $derived(run.done ? openGaps(ver) : []);
  let clean = $derived(run.done && open.length === 0);
  const max = atRisk(0);
  $effect(() => () => run.stop());

  function go() {
    if (run.done && !clean) ver += 1;
    run.start(() => (history = [...history, atRisk(ver)]));
  }
  function reset() {
    ver = 0;
    approved = false;
    history = [];
    run.start(() => (history = [...history, atRisk(ver)]));
  }
</script>

<section class="rt rt-band" {id}>
  <div class="rt-wrap">
    <div class="intro">
      <div>
        <p class="rt-kicker">Policy red-teaming</p>
        <h2>We built Azimuth to find flaws in code. Policy <span class="nowrap">red-teaming</span> finds the gaps in your agents’ rules.</h2>
        <p class="rt-lead">Policy red-teaming builds on TestMachine’s experience with Azimuth, its autonomous attacker agent. Every scenario that gets through your policy is a gap with a price: the money it could move. Each revision closes gaps and lowers that number.</p>
        <p class="rt-lead">Run the example below to follow a treasury policy from three priced gaps to none before the owner approves it.</p>
      </div>
      <div class="rt-azstats col">
        {#each azimuthStats as s}<div><b>{s.n}</b><span>{s.l}</span></div>{/each}
        <a class="az-link" href="/products/azimuth" onclick={() => trackCtaClick('home_redteam_azimuth', 'See how Azimuth works', '/products/azimuth')}>See how Azimuth works <span aria-hidden="true">→</span></a>
      </div>
    </div>

    <div class="rt-ink-panel console">
      <div class="meter">
        <p class="rt-cap">Example · money at risk in treasury policy {revisions[ver].v}</p>
        <p class="figure rt-mono" class:red={open.length > 0} class:gold={clean}>{run.done ? usd(atRisk(ver)) : '—'}</p>
        <p class="sub">{run.done ? (clean ? `All ${scenarioCount} scenarios pass` : `${open.length} open gap${open.length > 1 ? 's' : ''} out of ${scenarioCount} scenarios`) : run.running ? 'Running scenarios' : `${scenarioCount} attack scenarios ready`}</p>
        <ol class="hist">
          {#each revisions as r, i}
            <li>
              <span class="rt-mono">{r.v}</span>
              <div class="bar"><i class:zero={history[i] === 0} style="width: {history[i] === undefined ? 0 : history[i] === 0 ? 100 : (history[i] / max) * 100}%"></i></div>
              <b class="rt-mono">{history[i] === undefined ? '' : history[i] === 0 ? 'No gaps' : usd(history[i])}</b>
            </li>
          {/each}
        </ol>
        <div class="act">
          {#if run.running}
            <button class="rt-gold-btn" disabled>Running…</button>
          {:else if run.shown === 0}
            <button class="rt-gold-btn" onclick={go}>Run the scenarios</button>
          {:else if run.done && !clean}
            <button class="rt-gold-btn" onclick={go}>Apply {revisions[ver + 1].v} and run again</button>
          {:else if clean && !approved}
            <button class="rt-gold-btn" onclick={() => (approved = true)}>Approve {revisions[ver].v}</button>
          {:else if approved}
            <button class="rt-line-btn" onclick={reset}>Run again from v1</button>
          {/if}
        </div>
        <p class="gate">{approved ? `Approved. ${revisions[ver].v} is ready to assign.` : run.done && !clean ? `Next revision: ${revisions[ver + 1].change}` : 'Approval opens when every scenario passes.'}</p>
      </div>
      <div class="field">
        <AttackField items={scenarios(ver)} shown={run.shown} dark ok={clean} cols={12} size={9} label="{scenarioCount - open.length} of {scenarioCount} scenarios pass" />
      </div>
    </div>
    <p class="rt-note">Illustrative, simulated workflow.</p>
  </div>
</section>

<style>
  .intro { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: clamp(28px, 5vw, 64px); align-items: end; margin-bottom: clamp(36px, 4vw, 52px); }
  .nowrap { white-space: nowrap; }
  .az-link { justify-self: start; font: 500 0.95rem var(--font-body); color: var(--gold-light); text-decoration: none; border-bottom: 1px solid var(--gold-light); padding-bottom: 2px; }
  .az-link:hover { color: var(--ink-text); border-color: var(--ink-text); }
  .rt-azstats.col { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .console { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); }
  .meter { padding: 24px; border-right: 1px solid var(--ink-rule); }
  .figure { font-size: clamp(2.6rem, 6vw, 4.4rem); font-weight: 600; line-height: 1; margin-top: 12px; font-variant-numeric: tabular-nums; }
  .figure.red { color: var(--held-light); }
  .figure.gold { color: var(--gold-light); }
  .sub { font: 0.92rem var(--font-body); color: var(--ink-text-muted); margin-top: 10px; }
  .hist { list-style: none; display: grid; gap: 10px; margin-top: 26px; }
  .hist li { display: grid; grid-template-columns: 30px 1fr 90px; gap: 12px; align-items: center; font-size: 0.82rem; }
  .hist span { color: var(--ink-text-muted); }
  .hist b { font-weight: 500; text-align: right; font-variant-numeric: tabular-nums; }
  .bar { height: 4px; background: rgba(251, 250, 246, 0.08); border-radius: 2px; }
  .bar i { display: block; height: 100%; background: var(--held-light); border-radius: 2px; transition: width 0.6s; }
  .bar i.zero { background: var(--gold-light); opacity: 0.6; }
  .act { margin-top: 24px; min-height: 41px; }
  .act button { min-width: 230px; text-align: center; }
  .act button:disabled { opacity: 0.6; cursor: default; }
  .gate { margin-top: 12px; font: 0.88rem/1.5 var(--font-body); color: var(--ink-text-muted); min-height: 3em; }
  .field { padding: 24px; display: grid; place-items: center; }
  @media (max-width: 860px) {
    .intro, .console { grid-template-columns: minmax(0, 1fr); }
    .meter { border-right: 0; border-bottom: 1px solid var(--ink-rule); }
  }
</style>
